import ClearIcon from '@mui/icons-material/Clear';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Skeleton,
    Stack,
    Switch,
    TextField,
    Theme,
    Tooltip,
    Typography,
    alpha,
    debounce,
    useTheme,
} from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BackNavigationButton from '../../../components/common/BackNavigationButton';
import DialogAdaptive from '../../../components/common/DialogAdaptive';
import DialogContentAdaptive from '../../../components/common/DialogContentAdaptive';
import LargeMaterialUISwitch from '../../../components/common/LargeMaterialUISwitch';
import OnlyFansListsMultiSelector from '../../../components/common/OnlyFansListsMultiSelector';
import PageContainer from '../../../components/common/PageContainter';
import StyledCard from '../../../components/common/StyledCard';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import StyledDialogActions from '../../../components/common/StyledDialogActions';
import AutoSendWelcomeMessageDetailsForm from '../../../components/services/messageUsers/autoSendWelcomeMessage/AutoSendWelcomeMessageDetailsForm.refactored';
import AutoSendWelcomeMessageOnlineFansWelcomeMessageRow from '../../../components/services/messageUsers/autoSendWelcomeMessage/AutoSendWelcomeMessageOnlineFansWelcomeMessageRow';
import MessageUsersPriceBar from '../../../components/services/messageUsers/MessageUsersPriceBar';
import VoiceModelManager from '../../../components/services/tts/VoiceModelManagerDialog';
import useMessageUsersAutoWelcomeMessageOnlineUsers, {
    MessageUsersOnlineWelcomeMessage,
    MessageUsersOnlineWelcomeMessageSchema,
} from '../../../hooks/useMessageUsersAutoWelcomeMessageOnlineUsers';
import useMessageUsersAutoWelcomeMessageOnlineUsersSalesStats from '../../../hooks/useMessageUsersAutoWelcomeMessageOnlineUsersSalesStats';
import useMessageUsersAutoWelcomeMessageOnlineUsersStats from '../../../hooks/useMessageUsersAutoWelcomeMessageOnlineUsersStats';
import useSubscriber, { OnlyFansSubscriberMessageUsersAutoMessageOnlineUsers } from '../../../hooks/useSubscriber';
import { SettingsContext } from '../../../store/SettingsContext';
import { OnlyFansSubscriberMessageUsersAutoMessageDetails } from '../../../types/messageUsersAutoSendWelcomeMessage';
import { OnlyFansLists } from '../../../types/onlyFansTypes';
import { parseNumberOrNull } from '../../../utils/common';

const StatsCard = (props: { title: string; value: number | string | React.ReactNode; isLoading: boolean }) => {
    const { title, value, isLoading } = props;
    const theme: Theme = useTheme();

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: theme.shape.borderRadius,
                borderColor: 'divider',
                height: '100%',
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" sx={{ mb: 2 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            color: 'text.secondary',
                        }}
                    >
                        {title}
                    </Typography>
                    <RssFeedIcon
                        fontSize="small"
                        sx={{
                            color: 'primary.main',
                            animation: 'pulse 2s ease-in-out infinite',
                            '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                            },
                            // only take up the space it needs
                            flexShrink: 0,
                        }}
                    />
                </Stack>
                <Typography
                    key={value ? value.toString() : '0'}
                    variant="h4"
                    sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        fontSize: '2.5rem',
                        animation: 'slideUpFade 0.4s ease-out',
                        '@keyframes slideUpFade': {
                            '0%': {
                                transform: 'translateY(20px)',
                                opacity: 0,
                            },
                            '100%': {
                                transform: 'translateY(0)',
                                opacity: 1,
                            },
                        },
                    }}
                >
                    {isLoading ? <Skeleton width={100} height={50} /> : value?.toLocaleString() || 0}
                </Typography>
            </CardContent>
        </Card>
    );
};

const EarningsStatsCard = () => {
    const theme: Theme = useTheme();

    const { data: salesStats, isLoading: isSalesStatsLoading } = useMessageUsersAutoWelcomeMessageOnlineUsersSalesStats();

    const [amount, setAmount] = useState<number>(0);
    const [quantity, setQuantity] = useState<number>(0);
    const [resolution, setResolution] = useState<'1h' | '24h' | '1w' | '1m'>('1h');

    useEffect(() => {
        if (!salesStats) {
            setAmount(0);
            setQuantity(0);

            return;
        }

        switch (resolution) {
            case '1h':
                setAmount(salesStats.revenue1h || 0);
                setQuantity(salesStats.purchases1h || 0);
                break;

            case '24h':
                setAmount(salesStats.revenue24h || 0);
                setQuantity(salesStats.purchases24h || 0);

                break;

            case '1w':
                setAmount(salesStats.revenue1w || 0);
                setQuantity(salesStats.purchases1w || 0);

                break;

            case '1m':
                setAmount(salesStats.revenue1m || 0);
                setQuantity(salesStats.purchases1m || 0);

                break;
        }
    }, [resolution, salesStats]);

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: theme.shape.borderRadius,
                borderColor: 'divider',
                height: '100%',
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent={'space-between'} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: '0.95rem',
                                fontWeight: 500,
                                color: 'text.secondary',
                                flexGrow: 1,
                            }}
                        >
                            Earnings
                        </Typography>
                        <RssFeedIcon
                            fontSize="small"
                            sx={{
                                color: 'primary.main',
                                animation: 'pulse 2s ease-in-out infinite',
                                '@keyframes pulse': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.5 },
                                },
                            }}
                        />
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="1 Hour">
                            <Button
                                variant={resolution === '1h' ? 'contained' : 'text'}
                                onClick={() => setResolution('1h')}
                                sx={{
                                    width: '30px',
                                    minWidth: '30px',
                                }}
                            >
                                1h
                            </Button>
                        </Tooltip>
                        <Tooltip title="24 Hours">
                            <Button
                                variant={resolution === '24h' ? 'contained' : 'text'}
                                onClick={() => setResolution('24h')}
                                sx={{
                                    width: '30px',
                                    minWidth: '30px',
                                }}
                            >
                                24h
                            </Button>
                        </Tooltip>
                        <Tooltip title="1 Week">
                            <Button
                                variant={resolution === '1w' ? 'contained' : 'text'}
                                onClick={() => setResolution('1w')}
                                sx={{
                                    width: '30px',
                                    minWidth: '30px',
                                }}
                            >
                                1w
                            </Button>
                        </Tooltip>
                        <Tooltip title="1 Month">
                            <Button
                                variant={resolution === '1m' ? 'contained' : 'text'}
                                onClick={() => setResolution('1m')}
                                sx={{
                                    width: '30px',
                                    minWidth: '30px',
                                }}
                            >
                                1m
                            </Button>
                        </Tooltip>
                    </Stack>
                </Stack>
                <Typography
                    key={amount ? amount.toString() : '0'}
                    variant="h4"
                    sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        fontSize: '2.5rem',
                        animation: 'slideUpFade 0.4s ease-out',
                        '@keyframes slideUpFade': {
                            '0%': {
                                transform: 'translateY(20px)',
                                opacity: 0,
                            },
                            '100%': {
                                transform: 'translateY(0)',
                                opacity: 1,
                            },
                        },
                    }}
                >
                    {isSalesStatsLoading ? (
                        <Skeleton width={100} height={50} />
                    ) : (
                        `$${
                            amount?.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }) || 0
                        }`
                    )}
                </Typography>
            </CardContent>
        </Card>
    );
};

const MessageUsersAutoSendWelcomeMessageOnlineFans = () => {
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const dialog = useDialog();

    const [openAddNewMessage, setOpenAddNewMessage] = useState(false);

    const [onlineWelcomeMessagesQuery, setOnlineWelcomeMessageQuery] = useState<string>('');
    const [queryText, setQueryText] = useState<string>('');
    const [serviceActive, setServiceActive] = useState<boolean>(false);
    const [autoWelcomeMessageOnlineFansSettings, setAutoWelcomeMessageOnlineFansSettings] =
        useState<OnlyFansSubscriberMessageUsersAutoMessageOnlineUsers | null>(null);
    const [isAutoWelcomeMessageOnlineFansSettingsChanges, setIsAutoWelcomeMessageOnlineFansSettingsChanges] = useState<boolean>(false);
    const [autoUnsendAfterMinutes, setAutoUnsendAfterMinutes] = useState<number | null>(null);
    const [excludeLists, setExcludeLists] = useState<
        {
            id: number | OnlyFansLists.Type;
            name: string;
        }[]
    >([]);

    const {
        data: subscriber,
        isLoading: isSubscriberLoading,
        refetch: refetchSubscriber,
        setServiceRunning,
        setServiceRunningLoading,
        setServiceSettings,
    } = useSubscriber();

    const { data: autoWelcomeMessageOnlineFansStats, isLoading: isAutoWelcomeMessageOnlineFansStatsLoading } =
        useMessageUsersAutoWelcomeMessageOnlineUsersStats(serviceActive);

    useEffect(() => {
        if (subscriber) {
            const isActive =
                subscriber.messageUsers &&
                subscriber.messageUsers.autoMessageOnlineUsers &&
                subscriber.messageUsers.autoMessageOnlineUsers.active
                    ? true
                    : false;

            setServiceActive(isActive);
            setIsAutoWelcomeMessageOnlineFansSettingsChanges(false);
            setAutoUnsendAfterMinutes(subscriber.messageUsers?.autoMessageOnlineUsers?.autoUnsendAfterMinutes || null);

            setAutoWelcomeMessageOnlineFansSettings(subscriber.messageUsers?.autoMessageOnlineUsers || null);
        }
    }, [subscriber]);

    useEffect(() => {
        if (subscriber && autoWelcomeMessageOnlineFansSettings) {
            const currentSettings = subscriber.messageUsers?.autoMessageOnlineUsers;

            if (JSON.stringify(currentSettings) !== JSON.stringify(autoWelcomeMessageOnlineFansSettings)) {
                setIsAutoWelcomeMessageOnlineFansSettingsChanges(true);
            } else {
                setIsAutoWelcomeMessageOnlineFansSettingsChanges(false);
            }
        }
    }, [autoWelcomeMessageOnlineFansSettings]);

    const {
        data: autoMessages,
        refetch: refetchAutoMessages,
        isLoading: autoMessagesLoading,
        saveMessageOnlineUsersWelcomeMessage,
        isSavingAutoWelcomeMessageOnlineUsers,
        updateMessageOnlineUsersWelcomeMessage,
        isUpdatingAutoWelcomeMessageOnlineUsers,
        deleteMessageOnlineUsersWelcomeMessage,
        isDeletingAutoWelcomeMessageOnlineUsers,
        setMessageOnlineUsersWelcomeMessageActive,
    } = useMessageUsersAutoWelcomeMessageOnlineUsers(onlineWelcomeMessagesQuery);

    const initialMessageDetails = {
        active: true,
        delayBeforeSendingMessage: null,
        lockMessagePrice: null,
        lockedText: false,
        message: '',
        replaceUsername: null,
        mediaFiles: [],
        mediaFilesPreviews: [],
        taggedCreators: [],
        taggedReleaseForms: [],
        autoUnsendTimerPeriod: null,
        delayBetweenMessages: null,
        excludeLists: null,
        excludeSpentCondition: null,
        excludeSpentAmount: null,
        excludeSpentPeriod: null,
        excludeMessagedInPeriod: null,
        skipFansActive: false,
        skipFollowingActive: false,
        skipExpiredFans: false,
        addToList: null,
        tts: null,
    };

    // New Subscriber data
    const initialFormDataConst: MessageUsersOnlineWelcomeMessage = {
        subscriberId: '',
        name: '',
        messageDetails: initialMessageDetails,
        sentCount: 0,
    };

    const [initialFormData, setInitialFormData] = useState<MessageUsersOnlineWelcomeMessage>(initialFormDataConst);
    const [formData, setFormData] = useState<MessageUsersOnlineWelcomeMessage>(initialFormData);
    const [autoMessageInitialValues, setAutoMessageInitialValues] = useState<OnlyFansSubscriberMessageUsersAutoMessageDetails | null>(null);
    const [vaultAlbumName, setVaultAlbumName] = useState<string | undefined>();
    const [saveTemplate, setSaveTemplate] = useState<boolean>(false);
    const [saveTemplateName, setSaveTemplateName] = useState<string>('');
    const [autoMessageIsFormValid, setAutoMessageIsFormValid] = useState<boolean>(false);
    const [messageDetails, setMessageDetails] = useState<OnlyFansSubscriberMessageUsersAutoMessageDetails | null>(initialMessageDetails);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    // TTS Reference Models
    const [ttsModelManagerOpen, setTtsModelManagerOpen] = useState<boolean>(false);

    useEffect(() => {
        if (subscriber) {
            setFormData({ ...formData, subscriberId: subscriber._id });
            setInitialFormData({ ...initialFormData, subscriberId: subscriber._id });
        }
    }, [subscriber]);

    const addNewMessage = async () => {
        if (errors && errors.length > 0) {
            return;
        }

        if (formData && messageDetails && subscriber) {
            const result = await saveMessageOnlineUsersWelcomeMessage(subscriber._id, formData.name, messageDetails);

            if (result) {
                await refetchAutoMessages();
                resetForm();
                setOpenAddNewMessage(false);
                // happy banner noises
                enqueueSnackbar('Message Added Successfully!', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            } else {
                enqueueSnackbar('Failed to Add Message, please try again later.', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            }
        }
    };

    const editMessage = (item: MessageUsersOnlineWelcomeMessage) => {
        setIsEditing(true);
        setOpenAddNewMessage(true);
        setMessageDetails(item.messageDetails);
        setFormData(item);
    };

    const saveMessage = async () => {
        if (formData._id && messageDetails) {
            let currentFormData = { ...formData };

            currentFormData.messageDetails = messageDetails;

            const result = await updateMessageOnlineUsersWelcomeMessage(formData._id, currentFormData);

            if (result) {
                await refetchAutoMessages();

                setOpenAddNewMessage(false);

                resetForm();

                // display happy banner!
                enqueueSnackbar('Message Saved Successfully!', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            } else {
                enqueueSnackbar('Failed to Save Message, please try again later.', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            }
        }
    };

    const toggleActive = async (item: MessageUsersOnlineWelcomeMessage) => {
        if (item._id) {
            const result = await setMessageOnlineUsersWelcomeMessageActive(item._id, !item.messageDetails.active);

            await refetchAutoMessages();

            if (result) {
                // fire off happily updated message
                enqueueSnackbar('Message Successfully Toggled!', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            } else {
                enqueueSnackbar('Failed to Toggle Message, please try again later.', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            }
        }
    };

    const deleteMessage = async (item: MessageUsersOnlineWelcomeMessage) => {
        if (item._id) {
            const result = await deleteMessageOnlineUsersWelcomeMessage(item._id);

            resetForm();

            if (result.success) {
                // fire off happily deleted message
                await refetchAutoMessages();

                enqueueSnackbar('Message Successfully Deleted!', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            } else {
                // fire off unhappy message
                enqueueSnackbar('Failed to Delete Message, please try again later.', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            }
        }
    };

    const resetForm = () => {
        setFormData(initialFormData);
        setMessageDetails(initialMessageDetails);
        setIsEditing(false);
    };

    const errors = useMemo(
        () => MessageUsersOnlineWelcomeMessageSchema.validate(formData, { abortEarly: false, stripUnknown: true }).error?.details,
        [formData],
    );

    const updateOnlineWelcomeMessageQuery = (value: string) => {
        setOnlineWelcomeMessageQuery(value);
    };

    const searchQueryDebounce = useCallback(debounce(updateOnlineWelcomeMessageQuery, 750), []);

    const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Add a small delay to avoid too many requests
        // setTimeout(() => {
        //     setOnlineWelcomeMessageQuery(event.target.value);
        // }, 500);
        searchQueryDebounce(event.target.value);
        setQueryText(event.target.value);
    };

    const handleSaveSettings = async () => {
        if (subscriber) {
            const result = await setServiceSettings('messageUsers.autoMessageOnlineUsers', autoWelcomeMessageOnlineFansSettings);

            if (result) {
                enqueueSnackbar('Settings Saved Successfully!', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });

                refetchSubscriber();
            } else {
                enqueueSnackbar('Failed to Save Settings, please try again later.', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            }
        }
    };

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        <BackNavigationButton url={`/subscribers/${subscriber._id}/${settingsContext.services.messageUsers.homeUrl}`} />{' '}
                        Auto Welcome Message for {subscriber.username}
                    </Typography>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Auto Message Online Fans" />
                    </StyledCard>
                    <StyledCard>
                        <Typography gutterBottom>
                            Setup randomized welcome messages that get sent to a Fan shortly after they come online. When a fan comes
                            online, they will get sent one of the messages you setup below at random. The message will go to their Priority
                            inbox. It can include their username or AI voice note with you saying their name in your voice! Creators will be
                            skipped automatically.
                        </Typography>
                        <Typography gutterBottom>
                            You can also choose to have the system automatically unsend the message after a set period of time, helping to
                            keep your message history clean.
                        </Typography>
                        <Typography gutterBottom>
                            You can deactivate/reactivate individual messages without deleting them, allowing you to easily manage which
                            messages are active. So you can pre-setup messages for special occasions and activate them when needed!
                        </Typography>
                        <Typography gutterBottom sx={{ fontWeight: 'bold' }}>
                            Please note that this service is limited to sending a certain number of messages per minute to avoid rate
                            limiting by OnlyFans and to allow you to reply to fans. Sending priority messages will reduce how many messages
                            this service can send. On large pages where more fans come online than OnlyFans allow to message per minute,
                            some fans may not receive a message.
                        </Typography>
                        <Typography gutterBottom>
                            You can also enable Telegram Notifitations when a fan comes online in the{' '}
                            <Link to={`/subscribers/${subscriber._id}/${settingsContext.services.sextforce.homeUrl}/bigBrother/telegram`}>
                                Telegram Notifications
                            </Link>{' '}
                            service.
                        </Typography>
                        <MessageUsersPriceBar unitCost={0} includedWith="Sextforce" theme={theme} />
                    </StyledCard>

                    <StyledCard>
                        <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                            <Grid item xs>
                                Auto Messaging Online Fans is currently{' '}
                                {serviceActive ? (
                                    <span style={{ color: theme.palette.success.dark }}>running</span>
                                ) : (
                                    <span style={{ color: theme.palette.error.main }}>not running</span>
                                )}
                                .
                            </Grid>
                            <Grid item xs="auto">
                                {isSubscriberLoading || setServiceRunningLoading ? (
                                    <Skeleton width={'115px'} height={'51px'} />
                                ) : (
                                    <LargeMaterialUISwitch
                                        checked={serviceActive}
                                        onChange={e => {
                                            setServiceRunning('messageUsers.autoMessageOnlineUsers', e.target.checked);
                                            setServiceActive(e.target.checked);
                                        }}
                                        color="primary"
                                    />
                                )}
                            </Grid>
                        </Grid>
                        {/* Auto Unsend Setting */}
                        <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                            <Grid item xs={12}>
                                <Stack direction={'row'} spacing={1} alignItems="center" justifyContent={'space-between'}>
                                    <Stack direction={'column'} spacing={1} alignItems="flex-start">
                                        <Typography variant="body1">Unsend message?</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            If enabled, the system will automatically unsend welcome messages after a set number of minutes.
                                        </Typography>
                                    </Stack>
                                    <Switch
                                        checked={autoWelcomeMessageOnlineFansSettings?.autoUnsend || false}
                                        size="medium"
                                        onChange={e => {
                                            setAutoWelcomeMessageOnlineFansSettings(prevState => {
                                                return prevState
                                                    ? {
                                                          ...prevState,
                                                          autoUnsend: e.target.checked,
                                                          ...(!prevState.autoUnsendAfterMinutes && { autoUnsendAfterMinutes: 5 }),
                                                      }
                                                    : prevState;
                                            });
                                            setAutoUnsendAfterMinutes(e.target.checked ? 5 : null);
                                        }}
                                    />
                                </Stack>
                            </Grid>
                        </Grid>
                        {/* Auto Unsend Timer Period Setting */}
                        <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center" mt={1}>
                            <Grid item xs={12}>
                                <Stack direction={'row'} spacing={1} alignItems="center" justifyContent={'space-between'}>
                                    <Stack direction={'column'} spacing={1} alignItems="flex-start">
                                        <Typography variant="body1">Unsend After</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Set the period (in minutes) after which the welcome message will be automatically unsent.
                                        </Typography>
                                    </Stack>
                                    <TextField
                                        label="Minutes"
                                        disabled={!autoWelcomeMessageOnlineFansSettings?.autoUnsend}
                                        value={autoUnsendAfterMinutes || ''}
                                        onChange={e => {
                                            const value = parseNumberOrNull(e.target.value);

                                            if (value !== null && value > 0) {
                                                setAutoWelcomeMessageOnlineFansSettings(prevState => {
                                                    return prevState
                                                        ? { ...prevState, autoUnsendAfterMinutes: value ? undefined : value }
                                                        : prevState;
                                                });
                                            }

                                            setAutoUnsendAfterMinutes(value);
                                        }}
                                        error={
                                            autoWelcomeMessageOnlineFansSettings?.autoUnsend &&
                                            (autoUnsendAfterMinutes === null || autoUnsendAfterMinutes < 1)
                                        }
                                        sx={{
                                            width: '100px',
                                            minWidth: '100px',
                                        }}
                                    />
                                </Stack>
                            </Grid>
                        </Grid>
                        {/* Exclude Lists Setting */}
                        <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center" mt={1}>
                            <Grid item xs={12}>
                                <Stack direction={'column'} spacing={1} alignItems="flex-start">
                                    <Typography variant="body1">Exclude Users in Lists</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Select the lists of fans to exclude from receiving automatic welcome messages.
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                <OnlyFansListsMultiSelector
                                    targetLists={autoWelcomeMessageOnlineFansSettings?.excludeLists || []}
                                    setTargetLists={lists => {
                                        setAutoWelcomeMessageOnlineFansSettings(prevState => {
                                            return prevState
                                                ? {
                                                      ...prevState,
                                                      excludeLists: lists.length > 0 ? lists : undefined,
                                                  }
                                                : prevState;
                                        });
                                    }}
                                    listItemBackgroundColor={alpha(theme.palette.error.main, 0.3)}
                                    sx={{
                                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    }}
                                />
                            </Grid>
                        </Grid>
                        {/* Save Settings Button */}
                        <Box mt={2}>
                            <LoadingButton
                                fullWidth
                                variant="contained"
                                color={isAutoWelcomeMessageOnlineFansSettingsChanges ? 'secondary' : 'primary'}
                                size="large"
                                onClick={handleSaveSettings}
                                loading={isSubscriberLoading}
                                disabled={
                                    isSubscriberLoading ||
                                    !isAutoWelcomeMessageOnlineFansSettingsChanges ||
                                    (autoWelcomeMessageOnlineFansSettings?.autoUnsend &&
                                        (autoUnsendAfterMinutes === null || autoUnsendAfterMinutes < 1))
                                }
                            >
                                <span>Save Settings</span>
                            </LoadingButton>
                        </Box>
                    </StyledCard>

                    {/* Stats */}
                    <StyledCard noCard>
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={6}>
                                <StatsCard
                                    title="Total Messages Sent"
                                    value={
                                        serviceActive
                                            ? autoWelcomeMessageOnlineFansStats?.totalMessagesSent || 0
                                            : subscriber.messageUsers?.autoMessageOnlineUsers?.sentCount || 0
                                    }
                                    isLoading={isAutoWelcomeMessageOnlineFansStatsLoading}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <EarningsStatsCard />
                            </Grid>
                        </Grid>
                    </StyledCard>

                    <StyledCard noCard>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            fullWidth
                            onClick={() => {
                                setTtsModelManagerOpen(true);
                            }}
                        >
                            Manage Voice Models
                        </Button>
                        {ttsModelManagerOpen && (
                            <VoiceModelManager
                                open={ttsModelManagerOpen}
                                onClose={() => {
                                    setTtsModelManagerOpen(false);
                                }}
                                subscriber={subscriber}
                            />
                        )}
                    </StyledCard>

                    <StyledCard noCard>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            fullWidth
                            onClick={() => {
                                setOpenAddNewMessage(true);
                            }}
                        >
                            Add New Message
                        </Button>
                    </StyledCard>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar
                            title={`Welcome Messages (${(autoMessages?.length || 0).toLocaleString()})`}
                            isLoading={autoMessagesLoading}
                        />
                    </StyledCard>
                    <StyledCard noBottomMargin>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Search"
                                    value={queryText}
                                    onChange={handleQueryChange}
                                    onKeyDown={e => {
                                        // Clear on Escape
                                        if (e.key === 'Escape') {
                                            setOnlineWelcomeMessageQuery('');
                                            setQueryText('');
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: onlineWelcomeMessagesQuery.length > 0 && (
                                            <ClearIcon
                                                onClick={() => {
                                                    setOnlineWelcomeMessageQuery('');
                                                    setQueryText('');
                                                }}
                                                sx={{
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        ),
                                    }}
                                    sx={{
                                        backgroundColor: theme.palette.background.paper,
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={1} alignItems="center" mt={2}>
                            <Grid item xs={12}>
                                {!autoMessagesLoading && autoMessages?.length === 0 && (
                                    <Box textAlign="center" p={2} color={theme.palette.text.secondary}>
                                        {onlineWelcomeMessagesQuery === ''
                                            ? 'None currently setup. Click "Add New Message" to get started.'
                                            : 'No results found.'}
                                    </Box>
                                )}

                                {!autoMessagesLoading && autoMessages && autoMessages.length > 0 && (
                                    <Grid container spacing={1}>
                                        {autoMessages.map((welcomeMessage, index) => (
                                            <AutoSendWelcomeMessageOnlineFansWelcomeMessageRow
                                                key={welcomeMessage._id}
                                                index={index}
                                                welcomeMessage={welcomeMessage}
                                                editMessage={editMessage}
                                                toggleActive={toggleActive}
                                                deleteMessage={deleteMessage}
                                                isDeletingAutoWelcomeMessageOnlineUsers={isDeletingAutoWelcomeMessageOnlineUsers}
                                                isUpdatingAutoWelcomeMessageOnlineUsers={isUpdatingAutoWelcomeMessageOnlineUsers}
                                            />
                                        ))}
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </StyledCard>

                    <DialogAdaptive
                        open={openAddNewMessage}
                        onClose={() => {
                            resetForm();
                            setOpenAddNewMessage(false);
                        }}
                    >
                        <StyledCardTitleBar title={`${isEditing ? 'Edit Message Details' : 'Add New Message'}`} />
                        <DialogContentAdaptive>
                            {formData && (
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <Typography variant="body1">Name</Typography>
                                        <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                            Name this so it's recognisable
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            fullWidth
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            error={!!errors?.find(error => error.context?.key === 'name')}
                                            helperText={
                                                errors && errors.find(error => error.context?.key === 'name')
                                                    ? 'Name is required'
                                                    : undefined
                                            }
                                        />
                                    </Grid>
                                    {messageDetails && (
                                        <Grid item xs={12}>
                                            <AutoSendWelcomeMessageDetailsForm
                                                title="Active"
                                                description=""
                                                subscriber={subscriber}
                                                initialValues={autoMessageInitialValues}
                                                messageDetails={messageDetails}
                                                setMessageDetails={setMessageDetails}
                                                vaultAlbumName={vaultAlbumName}
                                                setVaultAlbumName={setVaultAlbumName}
                                                saveTemplate={saveTemplate}
                                                setSaveTemplate={setSaveTemplate}
                                                saveTemplateName={saveTemplateName}
                                                setSaveTemplateName={setSaveTemplateName}
                                                setIsFormValid={setAutoMessageIsFormValid}
                                                showTts={true}
                                                showInitialCheckBox={false}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </DialogContentAdaptive>
                        <StyledDialogActions>
                            <Button
                                color="secondary"
                                variant="outlined"
                                onClick={() => {
                                    dialog
                                        .confirm({
                                            message: 'Are you sure you want to cancel? You might have unsaved changes.',
                                            ok: {
                                                text: 'Yes',
                                                variant: 'contained',
                                                color: 'warning',
                                            },
                                            cancel: {
                                                text: 'No',
                                                variant: 'text',
                                                color: 'primary',
                                            },
                                        })
                                        .then(() => {
                                            setOpenAddNewMessage(false);
                                            resetForm();
                                        })
                                        .catch(() => {
                                            // do nothing on cancel
                                        });
                                }}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                color="primary"
                                variant="contained"
                                loading={isSavingAutoWelcomeMessageOnlineUsers || isUpdatingAutoWelcomeMessageOnlineUsers}
                                onClick={() => {
                                    if (isEditing) {
                                        dialog
                                            .confirm({
                                                message: 'Are you sure you want to save changes to this message?',
                                                ok: {
                                                    text: 'Yes',
                                                    variant: 'contained',
                                                    color: 'warning',
                                                },
                                                cancel: {
                                                    text: 'No',
                                                    variant: 'text',
                                                    color: 'primary',
                                                },
                                            })
                                            .then(() => {
                                                saveMessage();
                                            })
                                            .catch(() => {
                                                // do nothing on cancel
                                            });
                                    } else {
                                        addNewMessage();
                                    }
                                }}
                                disabled={(errors?.length ?? 0) > 0}
                            >
                                <span>{isEditing ? 'Save' : 'Add'}</span>
                            </LoadingButton>
                        </StyledDialogActions>
                    </DialogAdaptive>
                </>
            )}
        </PageContainer>
    );
};

export default MessageUsersAutoSendWelcomeMessageOnlineFans;
